import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useProjectMembers } from '@/hooks/use-projects';
import { cn } from '@/lib/utils';
import type { ProjectDetail } from '@hygge/shared';
import { Archive, ArchiveRestore, MoreHorizontal, Pencil, Users } from 'lucide-react';

const statusConfig: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  on_hold: { label: 'On Hold', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  archived: { label: 'Archived', className: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' },
};

interface ProjectHeaderProps {
  project: ProjectDetail;
  onEdit: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
}

export function ProjectHeader({ project, onEdit, onArchive, onUnarchive }: ProjectHeaderProps) {
  const formatName = useEmployeeNameFormatter();
  const status = statusConfig[project.status] || statusConfig.active;
  const initials = project.slug.slice(0, 2);
  const { data: members } = useProjectMembers(project.id);
  const projectManagers = (members ?? []).filter((m) => m.isManager && !m.removedAt);

  return (
    <div className="flex items-start gap-4">
      {/* Project icon */}
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-white font-bold text-lg shadow-sm"
        style={{ backgroundColor: project.color }}
      >
        {initials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">{project.name}</h1>
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', status.className)}>
            {status.label}
          </Badge>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
            {project.source === 'peopleforce' ? 'PeopleForce' : 'Manual'}
          </Badge>
          <span className="text-xs text-muted-foreground font-mono">{project.slug}</span>
        </div>

        {project.description && (
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{project.description}</p>
        )}

        <div className="flex items-center gap-4 mt-2.5">
          {/* Project managers */}
          {projectManagers.length > 0 && (
            <div
              className="flex items-center gap-1.5"
              title={projectManagers.map((m) => `${m.firstName} ${m.lastName}`).join(', ')}
            >
              <div className="flex -space-x-1.5">
                {projectManagers.slice(0, 3).map((pm) => (
                  <Avatar key={pm.id} className="h-5 w-5 ring-1 ring-background">
                    <AvatarImage src={pm.avatarUrl || undefined} />
                    <AvatarFallback className="text-[9px]">
                      {pm.firstName[0]}{pm.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                {formatName({ firstName: projectManagers[0].firstName, lastName: projectManagers[0].lastName })}
                {projectManagers.length > 1 && ` +${projectManagers.length - 1}`}
              </span>
            </div>
          )}

          {/* Members */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            <span>{project.memberCount} member{project.memberCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <DropdownMenu>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 rounded-full cursor-pointer">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Options</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onEdit} className="cursor-pointer">
            <Pencil />
            Edit Project
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {project.status === 'archived' ? (
            <DropdownMenuItem onClick={onUnarchive} className="cursor-pointer">
              <ArchiveRestore />
              Unarchive
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={onArchive} className="cursor-pointer">
              <Archive />
              Archive
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
